// Composable service used to make API request to the Java site
// todo; this could probably be refactored, but leaving it as one service for now to avoid early refactoring
import type {
  Country,
  FacetDict,
  FavoriteType,
  FeaturedContent,
  Product,
  ProductPagedResponse,
  ProductResponse,
  AddToCartAnalyticsContext
} from '~~/shared/types/common'
import { URL_CONFIG } from '~~/shared/utils/url.const'
import { CONTENT_ACTIONS } from '~~/shared/utils/contentActions'
import { CONTENT_TYPES } from '~~/shared/utils/contentTypes'
import { convertPriceStringToFloat } from '~~/shared/utils/price'
import { type CartLine, useCartStore } from "~/stores/cartStore";
import { useGtmStore } from '~/stores/googleTagManagerStore.js'
import { useLocaleStore } from '~/stores/localeStore'
import { useSecurityStore } from '~/stores/securityStore'
import { useUserStore } from '~/stores/userStore'

export default function () {
  const nuxtApp = useNuxtApp();
  const securityStore = useSecurityStore();
  const cartStore = useCartStore();
  const localeStore = useLocaleStore();

  type AddToCartProductLine = CartLine & {
    id?: number | string;
    unitPrice?: string | number;
    usingLoyaltyPoints?: boolean;
  };

  type AddToCartResponse = {
    lines?: AddToCartProductLine[];
  };

  const stringifyLogDetails = (details: Record<string, unknown>) => {
    const seen = new WeakSet<object>();

    return JSON.stringify(details, (_key, value) => {
      if (value instanceof Error) {
        return {
          name: value.name,
          message: value.message,
          stack: value.stack,
          cause: value.cause,
        };
      }

      if (value && typeof value === 'object') {
        if (seen.has(value)) return '[Circular]';
        seen.add(value);

        if ('message' in value || 'statusCode' in value || 'statusMessage' in value || 'status' in value || 'stack' in value) {
          const errorLike = value as {
            name?: unknown;
            message?: unknown;
            statusCode?: unknown;
            status?: unknown;
            statusMessage?: unknown;
            data?: unknown;
            stack?: unknown;
            cause?: unknown;
          };

          return {
            name: errorLike.name,
            message: errorLike.message,
            statusCode: errorLike.statusCode,
            status: errorLike.status,
            statusMessage: errorLike.statusMessage,
            data: errorLike.data,
            stack: errorLike.stack,
            cause: errorLike.cause,
          };
        }
      }

      return value;
    });
  };

  const logProductService = (message: string, details: Record<string, unknown>) => {
    console.info(`[siteService] ${message} ${stringifyLogDetails(details)}`);
  };

  const logProductServiceError = (message: string, details: Record<string, unknown>) => {
    console.error(`[siteService] ${message} ${stringifyLogDetails(details)}`);
  };

  const getProductResponseSize = (data: ProductPagedResponse | ProductResponse | undefined | null) => {
    if (!data) return 0;
    if ('data' in data && Array.isArray(data.data)) return data.data.length;
    if ('products' in data && Array.isArray(data.products)) return data.products.length;
    return 0;
  };

  const getAsyncDataError = (response: { error?: unknown }) => {
    const error = response.error;
    return error && typeof error === 'object' && 'value' in error ? error.value : error;
  };

  const trackProductAddedEvent = (
    productLineData: AddToCartProductLine | undefined,
    quantity: number,
    analyticsContext?: AddToCartAnalyticsContext | null
  ) => {
    if (!analyticsContext) return null;

    const gtmStore = useGtmStore();
    const userStore = useUserStore();
    const linePrice = convertPriceStringToFloat(productLineData?.unitPrice ?? productLineData?.subtotal);
    const value = analyticsContext.price ?? (Number.isFinite(linePrice) ? linePrice : undefined);

    gtmStore.pushEvent({ ecommerce: null });
    gtmStore.pushEvent({
      event: 'product_added',
      screen_name: analyticsContext.screen_name ?? 'Agenticplp_results',
      sub_screen: analyticsContext.sub_screen ?? 'Product_summary',
      Customer_id: userStore.data?.customerId ?? null,
      ecommerce: {
        currency: 'USD',
        value,
        items: [
          {
            item_id: analyticsContext.item_id ?? productLineData?.sku ?? null,
            item_name: analyticsContext.item_name ?? productLineData?.name ?? null,
            item_category: analyticsContext.item_category ?? null,
            price: value,
            quantity,
            item_list_id: analyticsContext.item_list_id ?? 'agentic_plp_results',
            item_list_name: analyticsContext.item_list_name ?? 'Agentic PLP Results'
          }
        ]
      }
    });
  };

  const trackClickstreamEvent = (
    productLineData: AddToCartProductLine | undefined,
    quantity: number,
    usingLoyaltyPoints: boolean,
    cartTotalAmount?: number
  ) => {
    globalThis.setTimeout(() => {
      try {
        const productId = productLineData?.productId ?? productLineData?.id;
        if (!productLineData?.sku || productId == null) return;

        const parsedPrice = convertPriceStringToFloat(productLineData.unitPrice);
        const payload: {
          product_id: string;
          sku: string;
          product_name: string;
          quantity: number;
          price?: number;
          currency: string;
          using_loyalty_points: boolean;
          total?: number;
        } = {
          product_id: String(productId),
          sku: productLineData.sku,
          product_name: productLineData.name,
          quantity: quantity,
          price: Number.isFinite(parsedPrice) ? parsedPrice : undefined,
          currency: 'USD',
          using_loyalty_points: productLineData.usingLoyaltyPoints ?? usingLoyaltyPoints,
          total: Number.isFinite(cartTotalAmount) ? cartTotalAmount : undefined
        };

        const result = nuxtApp.$hightouchEvents?.track?.('product_added', payload);

        void Promise.resolve(result).catch((error) => {
          console.warn('Failed to track product_added clickstream event', error);
        });
      } catch (error) {
        console.warn('Failed to track product_added clickstream event', error);
      }
    }, 0);
  };

  const favoriteApi = async <T>(
    url: string,
    options: Partial<Parameters<typeof $fetch>[1]> = {}
  ) => {
    if (!securityStore.data?.csrf) {
      await securityStore.fetch();
    }

    const csrf = securityStore.data?.csrf;
    if (!csrf) {
      throw new Error('CSRF token missing');
    }

    return await nuxtApp.$siteFetch<T>(url, {
      headers: {
        [csrf.headerName]: csrf.token,
        ...(options.headers || {})
      },
      ...options
    });
  };

  const customerAPI = async <T>() => {
    if (!securityStore.data?.csrf) {
      await securityStore.fetch();
    }

    const csrf = securityStore.data?.csrf;
    if (!csrf) {
      throw new Error('CSRF token missing');
    }

    return await nuxtApp.$siteFetch<T>(`${URL_CONFIG.CUSTOMER}`, {
      headers: {
        [csrf.headerName]: csrf.token
      }
    });
  };

  const buildFavoriteUrl = (type: FavoriteType, action?: 'favorite' | 'unfavorite') => {
    switch (action) {
      case CONTENT_ACTIONS.FAVORITE:
        return `${URL_CONFIG.FAVORITE}/${type}`;
      case CONTENT_ACTIONS.UNFAVORITE:
        return `${URL_CONFIG.UNFAVORITE}/${type}`;
      default:
        return type != CONTENT_TYPES.PRODUCT ? `${URL_CONFIG.FAVORITES}/${type}` : `${URL_CONFIG.FAVORITES}`;
    }
  };

  const formatMoney = (localeCode = 'en-US', currency = 'USD') => {
    return Intl.NumberFormat(localeCode, {
      style: 'currency',
      currency: currency
    });
  }

  return {
    getFavorites(type: FavoriteType) {
      return favoriteApi(buildFavoriteUrl(type), {
        method: 'GET'
      })
    },

    getCustomer() {
      return customerAPI()
    },

    favoriteProduct(itemId: number) {
      return favoriteApi(buildFavoriteUrl(CONTENT_TYPES.PRODUCT, CONTENT_ACTIONS.FAVORITE), {
        method: 'POST',
        body: { itemId }
      })
    },

    unfavoriteProduct(itemId: number) {
      return favoriteApi(buildFavoriteUrl(CONTENT_TYPES.PRODUCT, CONTENT_ACTIONS.UNFAVORITE), {
        method: 'POST',
        body: { itemId }
      })
    },

    favoriteItem(type: FavoriteType, builderId: string) {
      return favoriteApi(buildFavoriteUrl(type, CONTENT_ACTIONS.FAVORITE), {
        method: 'POST',
        body: { builderId }
      })
    },

    unfavoriteItem(type: FavoriteType, builderId: string) {
      return favoriteApi(buildFavoriteUrl(type, CONTENT_ACTIONS.UNFAVORITE), {
        method: 'POST',
        body: { builderId }
      })
    },

    fetchCountries(shippingOnly?: boolean) {
      // fetch countries; dedupes by default (cancels requests if one is already active)
      const dataKey = shippingOnly ? 'shipping-countries' : 'countries';
      return useAsyncData(dataKey, () => nuxtApp.$siteFetch<Country[]>('/geography/countries', {
        params: {
          format: "json",
          type: shippingOnly ? 'shipping' : undefined
        }
      }));
    },

    async fetchActiveProducts(
      locale: string = 'en-US',
      page: number = 0,
      filters: Record<string, string[]> = {},
      sort: string = 'recommendationRank,ASC'
    ){
      const dataToInclude = 'facets,sorting,relatedProducts'
      const pageNumber = page > 0 ? page - 1 : 0
      const pageSize = 23
      const params = new URLSearchParams({
        include: dataToInclude,
        size: String(pageSize),
        locale,
        page: String(pageNumber),
        sort: sort
      })

      for (const [facetType, values] of Object.entries(filters)) {
        for (const value of values) params.append('facet', `${facetType}:${value}`)
      }
      const url = `/api/v1/products?${params.toString()}`
      logProductService('fetchActiveProducts:start', {
        url,
        locale,
        page,
        pageNumber,
        pageSize,
        sort,
        filters,
      });

      const response = await useAsyncData(
        url,
        async () => {
          try {
            const data = await nuxtApp.$siteFetch<ProductPagedResponse>(url);
            logProductService('fetchActiveProducts:success', {
              url,
              locale,
              page,
              pageNumber,
              resultCount: getProductResponseSize(data),
              totalResults: data.page?.totalElements,
            });
            return data;
          } catch (error) {
            logProductServiceError('fetchActiveProducts:error', {
              url,
              locale,
              page,
              pageNumber,
              error,
            });
            throw error;
          }
        },
        {
          server: true,
          lazy: false,
        }
      )

      const asyncDataError = getAsyncDataError(response);
      if (asyncDataError) {
        logProductServiceError('fetchActiveProducts:asyncDataError', {
          url,
          locale,
          page,
          pageNumber,
          error: asyncDataError,
        });
      }

      return response
    },

    async fetchProductSets(
      setUrlKey: string,
      locale: string = 'en-US',
      page: number = 0,
      filters: Record<string, string[]> = {},
      sort: string = 'recommendationRank,ASC'
    ) {
      const dataToInclude = 'facets,sorting,relatedProducts'
      const pageNumber = page > 0 ? page - 1 : 0
      const pageSize = 23
      const params = new URLSearchParams({
        include: dataToInclude,
        size: String(pageSize),
        locale,
        page: String(pageNumber),
        sort
      })
      for (const [facetType, values] of Object.entries(filters)) {
        for (const value of values) params.append('facet', `${facetType}:${value}`)
      }

      const url = `/api/v1/products/sets/${encodeURIComponent(setUrlKey)}?${params.toString()}`
      logProductService('fetchProductSets:start', {
        url,
        locale,
        page,
        pageNumber,
        pageSize,
        sort,
        filters,
      });

      const response = await useAsyncData(
        url,
        async () => {
          try {
            const data = await nuxtApp.$siteFetch<ProductPagedResponse>(url);
            logProductService('fetchProductSets:success', {
              url,
              locale,
              page,
              pageNumber,
              resultCount: getProductResponseSize(data),
              totalResults: data.page?.totalElements,
            });
            return data;
          } catch (error) {
            logProductServiceError('fetchProductSets:error', {
              url,
              locale,
              page,
              pageNumber,
              error,
            });
            throw error;
          }
        },
        {
          server: true,
          lazy: false,
        }
      )

      const asyncDataError = getAsyncDataError(response);
      if (asyncDataError) {
        logProductServiceError('fetchProductSets:asyncDataError', {
          url,
          locale,
          page,
          pageNumber,
          error: asyncDataError,
        });
      }

      return response
    },

    fetchProductBySku(sku: string, locale: string = 'en-US') {
      return useAsyncData(`product-data-${sku}`, () => nuxtApp.$siteFetch<Product>(`/product/sku/${sku}`));
    },

    async fetchProductByAlias(alias: string,  locale: string = 'en-US') {
      const url = `/api/v1/products/alias/${alias}?locale=${locale}`
      logProductService('fetchProductByAlias:start', {
        url,
        alias,
        locale,
      });

      const response = await useAsyncData(
          url,
          async () => {
            try {
              const data = await nuxtApp.$siteFetch<ProductResponse>(url);
              logProductService('fetchProductByAlias:success', {
                url,
                alias,
                locale,
                resultCount: getProductResponseSize(data),
                selectedProductId: data.products?.[0]?.id,
                selectedProductSku: data.products?.[0]?.sku,
              });
              return data;
            } catch (error) {
              logProductServiceError('fetchProductByAlias:error', {
                url,
                alias,
                locale,
                error,
              });
              throw error;
            }
          },
          {
            server: true,
            lazy: false,
            transform: (data) => data.products[0],
          }
      );

      const asyncDataError = getAsyncDataError(response);
      if (asyncDataError) {
        logProductServiceError('fetchProductByAlias:asyncDataError', {
          url,
          alias,
          locale,
          error: asyncDataError,
        });
      }

      return response;
    },

    async fetchProductCrossSells(alias: string) {
      const url = `/api/v1/products/crossSells/${alias}`
      return useAsyncData(
          url,
          () => nuxtApp.$siteFetch<ProductResponse>(url),
          {
            server: true,
            lazy: true,
            transform: (data) => data.products
          }
      )
    },

    async fetchProductArticles(alias: string) {
      const url = `/api/v1/content/articles/product/${alias}`
      return useAsyncData(
          url,
          () => nuxtApp.$siteFetch<FeaturedContent[]>(url),
          {
            server: true,
            lazy: true,
          }
      )
    },

    async fetchProductsBySku(skus: string[]) {
      const url = `/api/v1/products?skus=${skus.join('&skus=')}`;
      return useAsyncData(url, () => nuxtApp.$siteFetch<ProductResponse>(url), {
        transform: (data) => {
          const products = data.products ?? [];
          products.forEach(p => p.price = formatMoney(localeStore.locale?.localeCode, p.priceCurrency).format(p.salePrice!));
          return products;
        }
      });
    },

    async addToCart(productId: number, quantity: number = 1, frequency: number = 0, usePoints: boolean = false, analyticsContext?: AddToCartAnalyticsContext | null) {
      if (!securityStore.data?.csrf) {
        await securityStore.fetch();
      }

      const csrf = securityStore.data?.csrf;
      if (!csrf) {
        throw new Error("Invalid CSRF token/data.");
      }

      return nuxtApp.$siteFetch<AddToCartResponse>(`/cart/item/${productId}`, {
        method: 'POST',
        headers: {
          [csrf.headerName]: csrf.token
        },
        body: {
          quantity,
          frequency,
          usePoints
        }
      }).then(async (res) => {
        await cartStore.fetch();
        const lines = cartStore.data?.lines;
        const cartTotalAmount = convertPriceStringToFloat(cartStore.data?.total);
        const lineAdded: CartLine | undefined = lines?.find((prd: CartLine) => prd.productId === productId);
        const responseLine = res.lines?.find(
          (prd) => prd.productId === productId || String(prd.id) === String(productId)
        );
        if (lineAdded) {
          useGtmStore().addToCartEvent(lineAdded);
          trackProductAddedEvent(responseLine ?? (lineAdded as AddToCartProductLine | undefined), quantity, analyticsContext);
          trackClickstreamEvent(responseLine ?? (lineAdded as AddToCartProductLine | undefined), quantity, usePoints, cartTotalAmount);
        }

        return res;
      });
    },

    async fetchBlogPosts(postType: string) {
      const url = `/api/v1/content/`
      return useAsyncData(
          url,
          () => nuxtApp.$siteFetch<ProductResponse>(url),
          {
            server: true,
            lazy: false,
            transform: (data) => data.products[0],
          }
      );
    },
  }
}
